import { useState, useMemo } from 'react'
import { DashboardLayout } from '../../components/layout'
import { StatCard, Button, Card } from '../../components/ui'
import { Select, Input } from '../../components/forms'
import {
  MonthlyBreakdown,
  CategoryPieChart,
  TrendChart,
  ProfitChart,
  ProfitLossStatement,
} from '../../features/reports'
import { useAllTransactions } from '../../hooks/useTransactions'
import { useBusinesses } from '../../hooks/useBusinesses'
import { formatCurrency, formatDateInput } from '../../lib/format'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

// Get date range presets
const getDatePresets = () => {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)
  const startOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)
  const startOfYear = new Date(now.getFullYear(), 0, 1)
  const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1)
  const endOfLastYear = new Date(now.getFullYear() - 1, 11, 31)

  return {
    thisMonth: { start: startOfMonth, end: now, label: 'This Month' },
    lastMonth: { start: startOfLastMonth, end: endOfLastMonth, label: 'Last Month' },
    thisQuarter: { start: startOfQuarter, end: now, label: 'This Quarter' },
    thisYear: { start: startOfYear, end: now, label: 'This Year' },
    lastYear: { start: startOfLastYear, end: endOfLastYear, label: 'Last Year' },
    allTime: { start: null, end: null, label: 'All Time' },
  }
}

function ReportsPage() {
  const { transactions, loading } = useAllTransactions()
  const { businesses } = useBusinesses()

  const [selectedBusiness, setSelectedBusiness] = useState('')
  const [datePreset, setDatePreset] = useState('thisYear')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [activeTab, setActiveTab] = useState('overview') // 'overview', 'pnl', 'trends'

  const datePresets = getDatePresets()

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    let filtered = transactions

    // Filter by business
    if (selectedBusiness) {
      filtered = filtered.filter(t => t.business_id === selectedBusiness)
    }

    // Filter by date
    let startDate, endDate

    if (datePreset === 'custom') {
      startDate = customStartDate ? new Date(customStartDate) : null
      endDate = customEndDate ? new Date(customEndDate) : null
    } else {
      const preset = datePresets[datePreset]
      startDate = preset.start
      endDate = preset.end
    }

    if (startDate || endDate) {
      filtered = filtered.filter(t => {
        const txDate = new Date(t.date)
        if (startDate && txDate < startDate) return false
        if (endDate && txDate > endDate) return false
        return true
      })
    }

    return filtered
  }, [transactions, selectedBusiness, datePreset, customStartDate, customEndDate])

  if (loading) {
    return (
      <DashboardLayout title="Reports">
        <LoadingSpinner.Overlay message="Loading reports..." />
      </DashboardLayout>
    )
  }

  // Calculate totals
  const revenue = filteredTransactions
    .filter(t => t.type === 'revenue' || t.type === 'income')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0)

  const expenses = filteredTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0)

  const profit = revenue - expenses
  const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0

  const businessOptions = [
    { value: '', label: 'All Businesses' },
    ...businesses.map(b => ({
      value: b.id,
      label: `${b.emoji} ${b.name}`,
    }))
  ]

  const dateOptions = [
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisQuarter', label: 'This Quarter' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'lastYear', label: 'Last Year' },
    { value: 'allTime', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' },
  ]

  // Get date range label
  const getDateRangeLabel = () => {
    if (datePreset === 'custom') {
      if (customStartDate && customEndDate) {
        return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`
      }
      return 'Custom Range'
    }
    return datePresets[datePreset]?.label || 'All Time'
  }

  // Get business name
  const getBusinessName = () => {
    if (!selectedBusiness) return 'All Businesses'
    const business = businesses.find(b => b.id === selectedBusiness)
    return business ? `${business.emoji} ${business.name}` : 'All Businesses'
  }

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Business', 'Type', 'Category', 'Description', 'Amount']
    const rows = filteredTransactions.map(t => [
      t.date,
      t.businesses?.name || '',
      t.type,
      t.category || '',
      t.description,
      t.amount,
    ])

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Print report
  const printReport = () => {
    window.print()
  }

  return (
    <DashboardLayout
      title="Reports"
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={printReport}>
            🖨️ Print
          </Button>
          <Button variant="secondary" onClick={exportToCSV}>
            📥 Export CSV
          </Button>
        </div>
      }
    >
      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div style={{ minWidth: '200px' }}>
            <label className="form-label">Business</label>
            <Select
              value={selectedBusiness}
              onChange={(e) => setSelectedBusiness(e.target.value)}
              options={businessOptions}
            />
          </div>
          <div style={{ minWidth: '180px' }}>
            <label className="form-label">Date Range</label>
            <Select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
              options={dateOptions}
            />
          </div>
          {datePreset === 'custom' && (
            <>
              <div>
                <label className="form-label">Start Date</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">End Date</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-4 mb-8">
        <StatCard
          label="Total Revenue"
          value={formatCurrency(revenue)}
        />
        <StatCard
          label="Total Expenses"
          value={formatCurrency(expenses)}
        />
        <StatCard
          label="Net Profit"
          value={formatCurrency(profit)}
          style={profit < 0 ? { borderColor: 'var(--error)' } : {}}
        />
        <StatCard
          label="Profit Margin"
          value={`${profitMargin.toFixed(1)}%`}
        />
      </div>

      {/* Tabs */}
      <div className="tabs mb-6">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'pnl' ? 'active' : ''}`}
          onClick={() => setActiveTab('pnl')}
        >
          P&L Statement
        </button>
        <button
          className={`tab ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          Trends
        </button>
        <button
          className={`tab ${activeTab === 'breakdown' ? 'active' : ''}`}
          onClick={() => setActiveTab('breakdown')}
        >
          Monthly Breakdown
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Trend Chart */}
          <div className="mb-6">
            <TrendChart transactions={filteredTransactions} months={12} />
          </div>

          {/* Category Charts */}
          <div className="grid grid-2 mb-6">
            <CategoryPieChart
              transactions={filteredTransactions}
              type="expense"
              title="Expenses by Category"
            />
            <CategoryPieChart
              transactions={filteredTransactions}
              type="revenue"
              title="Revenue by Category"
            />
          </div>

          {/* Profit Chart */}
          <div className="mb-6">
            <ProfitChart transactions={filteredTransactions} months={12} />
          </div>
        </>
      )}

      {activeTab === 'pnl' && (
        <ProfitLossStatement
          transactions={filteredTransactions}
          dateRange={getDateRangeLabel()}
          businessName={getBusinessName()}
        />
      )}

      {activeTab === 'trends' && (
        <>
          <div className="mb-6">
            <TrendChart
              transactions={filteredTransactions}
              title="Revenue & Expenses (12 Months)"
              months={12}
            />
          </div>
          <div className="mb-6">
            <ProfitChart
              transactions={filteredTransactions}
              title="Monthly Profit/Loss"
              months={12}
            />
          </div>
        </>
      )}

      {activeTab === 'breakdown' && (
        <MonthlyBreakdown transactions={filteredTransactions} />
      )}
    </DashboardLayout>
  )
}

export default ReportsPage
